'use client';

import { ChevronLeft, Send, X, Users } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { AIPrompt } from '@/components/ui/ai-prompt';
import { useRouter } from 'next/navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Footer from '../footer';
import { useIsMobile } from '@/lib/react-utils';
import { Session } from 'next-auth';
import AIUploadsClient from '../ai-uploads/ai-uploads-client';

export default function SearchComponent({ session }: { session: Session | null }) {
	const isMobile = useIsMobile(); // Use the custom hook
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const refSiteId = useRef<string | null>(null);
	const [companyPrompt, setCompanyPrompt] = useState('');

	const [searchStep, setSearchStep] = useState<'company' | 'people'>('company');
	const [companies, setCompanies] = useState<AISuggestedCompany[]>([]);
	const [peopleTypes, setPeopleTypes] = useState<string[]>([]);
	const [weights, setWeights] = useState<SearchWeight>({});
	const [inputValue, setInputValue] = useState('');
	const [extraLabel, setExtraLabel] = useState('');
	const [loadCount, setLoadCount] = useState(0);
	const [hasMore, setHasMore] = useState(true);
	const [loadMoreLoading, setLoadMoreLoading] = useState(false);

	const promptRef = useRef<HTMLInputElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);
	const router = useRouter();

	useEffect(() => {
		if (!session || !session?.siteId) {
			return;
		}
		refSiteId.current = session.siteId;
	}, [session]);

	const onCompanyPromptCallback = () => {
		handleSubmit();
	};

	const removePill = (type: 'company' | 'people', index: number) => {
		if (type === 'company') {
			setCompanies(companies.filter((_, i) => i !== index));
		} else {
			setPeopleTypes(peopleTypes.filter((_, i) => i !== index));
		}
	};

	const fetchMoreCompany = async (prompt: string) => {
		setLoadMoreLoading(true);
		const domainList = companies.map((company) => company.domain);
		try {
			const response = await fetch(
				`/api/sites/${refSiteId.current}/company-search/fringe?query=${prompt}&weights=${JSON.stringify(weights)}&domains=${JSON.stringify(domainList)}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json'
					}
				}
			);
			setLoadMoreLoading(false);
			if (!response.ok) {
				return;
			}
			setLoadCount(loadCount + 1);
			const results = await response.json();
			if (!results) {
				return;
			}
			if (results?.companies && results.companies.length > 0) {
				setCompanies([...companies, ...results.companies]);
				setWeights(results.weights as SearchWeight);
				// TODO: Should have some type of relevance cap
				// setHasMore(results.hasMore);
			}
			return;
		} catch (err: any) {
			setLoadMoreLoading(false);
		}
	};

	const fetchCompanyOld = async (prompt: string) => {
		try {
			const response = await fetch(
				`/api/sites/${refSiteId.current}/company-search?query=${prompt}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json'
					}
				}
			);
			if (!response.ok) {
				return;
			}
			const data = await response.json();
			if (data && data.length > 0) {
				setCompanies(data as AISuggestedCompany[]);
			}
			return;
		} catch (err: any) {}
	};

	const fetchCompany = async (prompt: string) => {
		try {
			const response = await fetch(
				`/api/sites/${refSiteId.current}/company-search/fringe?query=${prompt}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json'
					}
				}
			);
			if (!response.ok) {
				console.log(`Broken default to old`);
				await fetchCompanyOld(prompt);
				return;
			}
			setLoadCount(loadCount + 1);
			const results = await response.json();
			if (!results) {
				console.log(`Broken default to old`);
				await fetchCompanyOld(prompt);
				return;
			}
			if (results?.companies && results.companies.length > 0) {
				setCompanies(results.companies as AISuggestedCompany[]);
				setWeights(results.weights as SearchWeight);
			}
			return;
		} catch (err: any) {}
	};

	const fetchLeads = async (rolePrompt: string) => {
		try {
			const response = await fetch(`/api/sites/${refSiteId.current}/lead-search`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					companies,
					rolePrompt: rolePrompt,
					prompt: companyPrompt,
					weights: weights
				})
			});
			if (!response.ok) {
				return;
			}
			const data = await response.json();
			if (!data.aiUpload) {
				alert('Creation failed, please try again later');
				return;
			}
			router.push(`/ai-uploads/${data.aiUpload.id}`);
			return;
		} catch (err: any) {}
	};

	const handleSubmit = async () => {
		if (inputValue.trim()) {
			if (searchStep === 'company') {
				setIsLoading(true);
				setCompanyPrompt(inputValue.trim());
				await fetchCompany(inputValue.trim());
				setSearchStep('people');
				if (!isMobile) {
					setExtraLabel('Generate leads');
				}
				setIsLoading(false);
			} else {
				setIsLoading(true);
				await fetchLeads(inputValue.trim());
				setIsLoading(false);
			}
			setInputValue('');
		}
	};

	const handleClickScroll = (element: React.MouseEvent<HTMLDivElement>) => {
		if (searchStep !== 'people') {
			if (imageRef.current && element.currentTarget?.getAttribute('data-prompt')) {
				imageRef.current.scrollIntoView({ behavior: 'smooth' });
				console.log(element.currentTarget.getAttribute('data-prompt'));
				setInputValue(element.currentTarget.getAttribute('data-prompt') || '');
				setTimeout(() => {
					const event = new KeyboardEvent('keydown', { key: 'Enter', bubbles: true });
					promptRef.current?.dispatchEvent(event);
				}, 500);
			}
		} else {
			setSearchStep('company');
			setExtraLabel('');
			setCompanies([]);
			setLoadCount(0);

			if (imageRef.current && element.currentTarget?.getAttribute('data-prompt')) {
				imageRef.current.scrollIntoView({ behavior: 'smooth' });
				console.log(element.currentTarget.getAttribute('data-prompt'));
				setInputValue(element.currentTarget.getAttribute('data-prompt') || '');
				setTimeout(() => {
					const event = new KeyboardEvent('keydown', { key: 'Enter', bubbles: true });
					promptRef.current?.dispatchEvent(event);
				}, 500);
			}
		}
	};

	const promptExamples = [
		{
			prompt: 'Running shoes store that struggles with refunds',
			companies: ['shoemall.com', 'restocks.com', 'bkfootwear.com'],
			companyCount: 100
		},
		{
			prompt: 'Ecommerce struggling with slow customer service response time',
			companies: ['carbon38.com', 'bestseller.com', 'lollicouture.com'],
			companyCount: 100
		},
		{
			prompt: 'D2C Ecommerce that sells fashion clothes to Gen Z and young adults',
			companies: ['zemato.co', 'nohble.com', 'orugastore.com'],
			companyCount: 100
		},
		{
			prompt: 'Veterinary Clinics & Animal Hospitals',
			companies: ['dogwoodvet.ca', 'chestermerevet.com', 'qualicumbeachvet.com'],
			companyCount: 97
		},
		{
			prompt: 'Cleaning & Janitorial Services',
			companies: ['spotlessandsorted.com', 'cleanou.com', 'eco-fresh.co.uk'],
			companyCount: 98
		},
		{
			prompt: 'Advertising agencies with specialization in experiential marketing',
			companies: ['urbanbella.ca', 'goodkids.ca', 'dartdesign.com '],
			companyCount: 97
		},
		{
			prompt: 'Resale marketplaces for luxury goods',
			companies: ['northshoreexchange.org', 'comptoirvintage.com', 'gemaee.com'],
			companyCount: 66
		},
		{
			prompt: 'VC investing in pre-seed healthcare startups',
			companies: ['pitango.com', '2048.vc', 'foothill.ventures'],
			companyCount: 100
		},
		{
			prompt: 'Spas & Wellness Centers',
			companies: ['www.skinnv.com', 'riversidespa.ca', 'casaspa.co.uk'],
			companyCount: 86
		},
		{
			prompt: 'Hospitals & Healthcare Facilities',
			companies: ['bchcares.org', 'induscancer.com', 'nuffieldhealth.com'],
			companyCount: 79
		},
		{
			prompt: 'Dental & Orthodontic Clinics',
			companies: ['dentystabirmingham.co.uk', '10dental.co.uk', 'arlingtondentalma.com'],
			companyCount: 90
		},
		{
			prompt: 'Nursing Homes & Long-Term Care Facilities',
			companies: ['carehomemedical.co.uk', 'caringhomes.org', 'dovehillvillage.com'],
			companyCount: 88
		},
		{
			prompt: 'Construction & Industrial Manufacturing',
			companies: ['firstclassconstruction.ca', 'arkbuild.london', 'snowdonconstruction.com'],
			companyCount: 88
		},
		{
			prompt: 'Food Catering Companies',
			companies: ['copleysfood.co.uk', 'messinascatering.com', 'cocktailsandcanapes.ca'],
			companyCount: 88
		}
	];

	// Get Query Params
	useEffect(() => {
		const url = new URL(window.location.href);
		const query = url.searchParams.get('query');
		if (query && promptRef?.current) {
			setInputValue(query);
			setTimeout(() => {
				const event = new KeyboardEvent('keydown', { key: 'Enter', bubbles: true });
				promptRef.current?.dispatchEvent(event);
			}, 500);
		}
	}, []);

	return (
		<>
			<div className="w-full lg:max-w-none max-w-4xl mx-auto overflow-hidden bg-gradient-to-br">
				<section className="w-full py-16 bg-gradient-to-r from-[#01beb7] to-[#33e66c]">
					<div className="container px-4 md:px-6 text-center">
						<div className="space-y-4">
							<div className="container flex justify-center">
								<div className="flex flex-row items-center space-y-4 space-x-4">
									<Image
										src="/nos_small.png"
										alt="NOS email logo"
										width={120}
										height={60}
										className="w-24"
										ref={imageRef}
									/>
									<div className="h-16 border-l-2 border-white"></div>
									<h1 className="text-3xl sm:text-2xl lg:text-4xl text-white leading-tight text-left">
										{isMobile ? (
											<>
												B2B Outreach
												<br className="hidden sm:inline" /> Supercharged
											</>
										) : (
											<>
												Supercharge Your
												<br className="hidden sm:inline" /> B2B Outreach
											</>
										)}
									</h1>
								</div>
							</div>
							{searchStep === 'company' && (
								<div className="space-y-2 max-w-2xl mx-auto text-left">
									<span className="ml-0 text-lg font-light">Step 1 of 2</span>
									<div className="space-y-4">
										<div className="flex items-center space-x-3 text-[#01354a]">
											<Send className="h-6 w-6 text-[#01354a]" />
											<span className="text-xl font-medium">
												What businesses do you want to target?
											</span>
										</div>
									</div>
								</div>
							)}
							{searchStep === 'people' && (
								<div className="space-y-2 max-w-2xl mx-auto text-left">
									<Button
										onClick={() => {
											setSearchStep('company');
											setExtraLabel('');
											setCompanies([]);
										}}
										variant="ghost"
										className="px-0 flex items-center gap-2 text-emerald-950 rounded-md transition-all duration-300 ease-in-out group hover:bg-inherit"
									>
										<ChevronLeft className="h-5 w-5 transition-transform duration-300 ease-in-out group-hover:-translate-x-0.5" />
										<span className="text-lg font-medium">Back</span>
										<span className="ml-4 text-lg font-light">Step 2 of 2</span>
									</Button>
									<div className="space-y-4">
										<div className="flex items-center space-x-3 text-[#50ab87]">
											<div className="rounded-full bg-green-400 text-white flex items-center justify-center">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-6 w-6"
													viewBox="0 0 20 20"
													fill="currentColor"
													width={20}
													height={20}
												>
													<path
														fillRule="evenodd"
														d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
														clipRule="evenodd"
													/>
												</svg>
											</div>
											<span className="text-xl font-medium text-">
												What businesses do you want to target?
											</span>
										</div>
										<div className="flex items-center space-x-3 text-[#01354a]">
											<Users className="h-6 w-6 text-[#01354a]" />
											<span className="text-xl font-medium">
												Which roles do you want to reach out to?
											</span>
										</div>
									</div>
								</div>
							)}
							<AIPrompt
								placeholder={
									searchStep === 'company'
										? isMobile
											? 'e.g. Biz that sell cat toys' // Placeholder text for mobile
											: 'Example: Businesses that sell cat toys' // Placeholder text for desktop
										: isMobile
											? 'e.g. HR Execs' // Placeholder text for mobile
											: 'Example: HR Executives' // Placeholder text for desktop
								}
								value={inputValue}
								ref={promptRef}
								onChange={(e) => setInputValue(e.target.value)}
								onCallback={onCompanyPromptCallback}
								extra_label={extraLabel}
								isLoading={isLoading}
							></AIPrompt>
							{(companies.length > 0 || peopleTypes.length > 0) && (
								<div className="bg-white rounded-lg p-4 space-y-2 max-w-2xl mx-auto text-left">
									{companies.length > 0 && (
										<div>
											<div className="flex items-center space-x-4 text-center mb-2">
												<Button
													onClick={() => {
														setSearchStep('company');
														setExtraLabel('');
														setCompanies([]);
													}}
													variant="ghost"
													className="px-0 flex items-center gap-2 text-emerald-950 rounded-md py-2  transition-all duration-300 ease-in-out group hover:bg-inherit"
												>
													<ChevronLeft className="h-5 w-5 transition-transform duration-300 ease-in-out group-hover:-translate-x-0.5" />
													<span className="font-bold">Back</span>
												</Button>
												<p className="font-semibold text-left">
													{companyPrompt}
												</p>
											</div>
											<div className="flex flex-wrap gap-2">
												{companies.map((company, index) => (
													<span
														key={index}
														className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full flex items-center"
													>
														<a
															href={`https://${company.domain}`}
															className="text-teal-800 hover:underline"
															target="_blank"
														>
															{company.domain}
															<span>
																{` `}({company.relevance}% )
															</span>
														</a>
														<button
															onClick={() =>
																removePill('company', index)
															}
															className="ml-2"
														>
															<X size={14} />
														</button>
													</span>
												))}
											</div>
											{hasMore && (
												<Button
													onClick={() => fetchMoreCompany(companyPrompt)}
													variant="outline"
													className="mt-4 w-full"
													disabled={loadMoreLoading}
												>
													{loadMoreLoading ? (
														<div className="flex items-center justify-center">
															<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
														</div>
													) : (
														`Load More`
													)}
												</Button>
											)}
										</div>
									)}
									{peopleTypes.length > 0 && (
										<div>
											<h3 className="font-semibold mb-2">People Types:</h3>
											<div className="flex flex-wrap gap-2">
												{peopleTypes.map((type, index) => (
													<span
														key={index}
														className="bg-green-100 text-green-800 px-3 py-1 rounded-full flex items-center"
													>
														{type}
														<button
															onClick={() =>
																removePill('people', index)
															}
															className="ml-2"
														>
															<X size={14} />
														</button>
													</span>
												))}
											</div>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</section>
				<section className="w-full py-12 bg-inherit">
					<div className="container grid items-center justify-center gap-6 px-4 md:px-6 lg:gap-10">
						<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
							{promptExamples.map((prompt, index) => (
								<Card
									key={index}
									onClick={handleClickScroll}
									data-prompt={prompt.prompt}
								>
									<div className="p-6 h-full rounded-lg bg-background shadow-lg cursor-pointer transform transition-all duration-300 hover:shadow-xl hover:scale-105 active:scale-95">
										<h3 className="text-xl font-semibold text-black">
											Try search:{' '}
											<strong className="prompt-text">{prompt.prompt}</strong>
										</h3>
										<div className="flex flex-wrap gap-2 my-4">
											{prompt.companies.map((company, index) => (
												<span
													key={index}
													className="inline-block bg-[#01beb7] text-white text-sm font-semibold py-1 px-3 rounded-full"
												>
													{company}
												</span>
											))}
										</div>
										<p className="text-muted-foreground text-sm">
											...and {prompt.companyCount} more companies found
										</p>
									</div>
								</Card>
							))}
						</div>
					</div>
				</section>
				<Card className="w-full max-w-4xl mx-auto overflow-hidden bg-gradient-to-br mt-2">
					<AIUploadsClient session={session} />
				</Card>
			</div>
			<Footer />
		</>
	);
}
